import React, { useState } from "react";
import {
  Card,
  Checkbox,
  Radio,
  DatePicker,
  Select,
  Button,
  Divider,
  Row,
  Col,
  notification,
  Modal,
  Form,
} from "antd";
import { ImportOutlined, DeleteOutlined } from "@ant-design/icons";
import apiClient from 'services/apiClient';
import { useMyContext } from "Context/MyContextProvider";
import { UserSelectField } from "../CustomUtils.js/CommonFieldUtils";
import CleanUpHistory from "./CleanUpHistory";
import { ROW_GUTTER } from "constants/ThemeConstant";

const { RangePicker } = DatePicker;
const { Option } = Select;

const CleanUp = () => {
  const { api, authToken, UserData } = useMyContext();
  // Form instance
  const [form] = Form.useForm();

  // Loading states
  const [loading, setLoading] = useState(false);
  // Progress modal states
  const [progressVisible, setProgressVisible] = useState(false);
  const [currentAction, setCurrentAction] = useState("");
  const [totalSteps, setTotalSteps] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [updateState, setUpdateState] = useState(0);
  // For controlling date range selection type
  const [dateRangeType, setDateRangeType] = useState("preset");

  // Handle date range type selection
  const handleDateRangeTypeChange = (e) => {
    setDateRangeType(e.target.value);
    // Reset the other field when switching between date range types
    if (e.target.value === "custom") {
      form.setFieldsValue({ presetRange: undefined });
    } else {
      form.setFieldsValue({ dateRange: undefined });
    }
  };

  // Format date parameters based on selection
  const getDateParams = (formValues) => {
    if (dateRangeType === "custom" && formValues.dateRange) {
      return {
        start_date: formValues.dateRange[0].format("YYYY-MM-DD"),
        end_date: formValues.dateRange[1].format("YYYY-MM-DD"),
      };
    } else {
      return {
        date_range: formValues.presetRange,
      };
    }
  };

  // Handle submission - exports and cleans up in a single action
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!values.selectedTables || values.selectedTables.length === 0) {
        notification.warning({
          message: "No tables selected",
          description: "Please select at least one table to process.",
        });
        return;
      }
      await handleAllOperations(values);
    } catch (error) {
      console.log("Form validation failed:", error);
    }
  };

  // Handle all operations - process one table completely before moving to the next
  const handleAllOperations = async (formValues) => {
    const dateParams = getDateParams(formValues);
    const selectedTables = formValues.selectedTables;
    const selectedUserId = formValues.userId;

    // Include userId in params if selected
    const params = {
      ...dateParams,
      action_by: UserData?.id,
      time_period: dateRangeType === "preset",
      user_id: selectedUserId ? selectedUserId : null,
    };

    // Table configuration with steps and operations
    const tableConfig = {
      report: {
        steps: 2,
        operations: [
          {
            action: "Exporting Incoming Reports",
            endpoint: "cleanup/reports/export",
            method: "get",
          },
          {
            action: "Cleaning Up Incoming Reports",
            endpoint: "cleanup/reports",
            method: "delete",
          },
        ],
      },
      outReport: {
        steps: 2,
        operations: [
          {
            action: "Exporting Out Reports",
            endpoint: "cleanup/outreports/export",
            method: "get",
          },
          {
            action: "Cleaning Up Out Reports",
            endpoint: "cleanup/outreports",
            method: "delete",
          },
        ],
      },
      campaign: {
        steps: 3,
        operations: [
          {
            action: "Exporting Campaigns",
            endpoint: "cleanup/campaigns/export",
            method: "get",
          },
          {
            action: "Exporting Campaign Reports",
            endpoint: "cleanup/campaign-reports/export",
            method: "get",
          },
          {
            action: "Cleaning Up Campaigns",
            endpoint: "cleanup/campaigns",
            method: "delete",
          },
        ],
      },
    };

    // Calculate total steps
    const totalStepsCount = selectedTables.reduce((total, table) => {
      return total + (tableConfig[table]?.steps || 0);
    }, 0);

    // Initialize progress tracking
    setTotalSteps(totalStepsCount);
    setCurrentStep(0);
    setProgressVisible(true);
    setLoading(true);

    try {
      // Process each selected table
      for (const table of selectedTables) {
        const config = tableConfig[table];
        if (!config) {
          console.warn(`Unknown table: ${table}`);
          continue;
        }

        // Execute all operations for the current table
        for (const operation of config.operations) {
          setCurrentAction(operation.action);
          await apiClient({
            method: operation.method,
            url: `/${operation.endpoint}`,
            params: params,
          })
            .then((response) => {
              const responseData = response.data.data;
              console.log(responseData);

              // Check if the response is a URL (file link) and initiate download
              if (
                typeof responseData === "string" &&
                (responseData.startsWith("http://") ||
                  responseData.startsWith("https://") ||
                  responseData.startsWith("/uploads/") ||
                  responseData.includes("/storage/uploads/") ||
                  responseData.includes("/exports/"))
              ) {
                // If it's a relative path, construct the full URL
                const fileUrl = responseData.startsWith("/")
                  ? `${window.location.origin}${responseData}`
                  : responseData;

                // Force the browser to treat this as a download by opening in an invisible iframe
                // This approach prevents double downloads that can happen with the link method
                const iframe = document.createElement("iframe");
                iframe.style.display = "none";
                iframe.src = fileUrl;
                document.body.appendChild(iframe);

                // Remove the iframe after a delay to ensure download starts
                setTimeout(() => {
                  document.body.removeChild(iframe);
                }, 2000);
              }
            })
            .catch((error) => {
              console.error("Error during operation:", error);
            });

          setCurrentStep((prev) => prev + 1);
        }
      }

      notification.success({
        message: "Operation Successful",
        description: "Data has been exported and cleaned up successfully.",
      });
    } catch (error) {
      console.error("Operation failed:", error);
      notification.error({
        message: "Operation Failed",
        description:
          error.response?.data?.message ||
          error.message ||
          "Failed to process data. Please try again.",
      });
    } finally {
      setUpdateState((prev) => prev + 1); // Trigger a re-render to update the history
      setProgressVisible(false);
      setLoading(false);
    }
  };

  // Options for table selection
  const tableOptions = [
    { label: "Out Reports", value: "outReport" },
    { label: "Incoming Reports", value: "report" },
    { label: "Campaigns (with campaign reports)", value: "campaign" },
  ];
  const HandleUserChange = (value) => {
    console.log(value);
  };
  return (
    <div>
      {/* Progress Modal */}
      <Modal
        title="Operation in Progress"
        open={progressVisible}
        footer={null}
        closable={false}
      >
        <div style={{ textAlign: "center" }}>
          {currentAction.toLowerCase().includes("exporting") ? (
            <ImportOutlined
              spin
              style={{ fontSize: "36px", color: "#1890ff" }}
            />
          ) : (
            <DeleteOutlined
              spin
              style={{ fontSize: "36px", color: "#ff4d4f" }}
            />
          )}
          <p style={{ marginTop: "15px", fontSize: "16px" }}>{currentAction}</p>
          <p style={{ textAlign: "center", marginTop: "20px" }}>
            {`${currentStep} of ${totalSteps} tasks completed`}
          </p>
        </div>
      </Modal>
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <Card title="Data Clean Up">
            <div
              className="feature-description"
              style={{ marginBottom: "20px" }}
            >
              <h4
                style={{
                  fontWeight: 500,
                  color: "#1890ff",
                  marginBottom: "12px",
                }}
              >
                Export and Clean Your WhatsApp Business Data
              </h4>
              <p
                style={{
                  fontSize: "14px",
                  lineHeight: "1.6",
                  marginBottom: "8px",
                }}
              >
                This feature allows you to export historical data from your
                WhatsApp Business account based on your specified date range.
                You can select which data types to include:
              </p>
              <ul style={{ paddingLeft: "20px", marginBottom: "12px" }}>
                <li style={{ marginBottom: "6px" }}>
                  <strong>Outgoing Reports</strong> - Messages sent from your
                  account to customers
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <strong>Incoming Reports</strong> - Messages received from
                  customers
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <strong>Campaign Data</strong> - Marketing campaigns and their
                  performance reports
                </li>
              </ul>
              <p
                style={{
                  fontSize: "14px",
                  backgroundColor: "#e6f7ff",
                  padding: "10px",
                  borderLeft: "4px solid #1890ff",
                  borderRadius: "2px",
                }}
              >
                All exported files will be downloaded automatically in CSV
                format once the operation completes.
              </p>
            </div>

            <Divider style={{ margin: "16px 0" }} />
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Form.Item name="userId" label="Select User (Optional)">
                <UserSelectField
                  HandleUserChange={HandleUserChange}
                />
              </Form.Item>

              <Form.Item
                name="selectedTables"
                label="Select Tables to Clean Up"
                rules={[
                  {
                    required: true,
                    message: "Please select at least one table",
                  },
                ]}
              >
                <Checkbox.Group options={tableOptions} />
              </Form.Item>

              <Divider />

              <Form.Item label="Select Date Range">
                <Radio.Group
                  value={dateRangeType}
                  onChange={handleDateRangeTypeChange}
                  style={{ marginBottom: "16px" }}
                >
                  <Radio value="custom">Custom Date Range</Radio>
                  <Radio value="preset">Preset Periods</Radio>
                </Radio.Group>

                <div style={{ marginTop: "16px" }}>
                  {dateRangeType === "custom" ? (
                    <Form.Item
                      name="dateRange"
                      rules={[
                        {
                          required: true,
                          message: "Please select a date range",
                        },
                      ]}
                      noStyle
                    >
                      <RangePicker style={{ width: "100%" }} />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="presetRange"
                      initialValue="olderThanCurrentMonth"
                      rules={[
                        {
                          required: true,
                          message: "Please select a preset period",
                        },
                      ]}
                      noStyle
                    >
                      <Select style={{ width: "100%" }}>
                        <Option value="olderThanCurrentMonth">
                          Older Than Current Month
                        </Option>
                        <Option value="olderThan3Months">
                          Older Than 3 Months
                        </Option>
                        <Option value="olderThan6Months">
                          Older Than 6 Months
                        </Option>
                        <Option value="olderThan1Year">
                          Older Than 1 Year
                        </Option>
                      </Select>
                    </Form.Item>
                  )}
                </div>
              </Form.Item>

              <Form.Item style={{ marginTop: "20px", textAlign: "right" }}>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Submit
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <Card>
            <CleanUpHistory
              api={api}
              authToken={authToken}
              updateState={updateState}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CleanUp;
